/**
 * Login Hook
 * 로그인 관련 로직 관리
 */
import {useState, useCallback, useEffect, use} from 'react';
import { useNavigate } from 'react-router-dom';
import { login, adminLogin } from '../api/loginApi';
import { LoginRequest, LoginResponse, LoginType, SupplierLoginResponse } from '../types/login';
import { setCookie } from '../../../core/utils';
import { useAuth } from '../../../core/contexts/AuthContext';
import { loginAttemptTracker, type AttemptStatus } from '../../../core/utils/loginAttemptTracker';

export const useLogin = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loginType, setLoginType] = useState<LoginType>('rider');
    const [lockStatus, setLockStatus] = useState<AttemptStatus>(() => loginAttemptTracker.getStatus());
    const navigate = useNavigate();
    const { setAuth } = useAuth();

    // 잠금 중일 때 1초마다 카운트다운 갱신
    useEffect(() => {
        if (!lockStatus.isLocked) return;
        const timer = setInterval(() => {
            const status = loginAttemptTracker.getStatus();
            setLockStatus(status);
            if (!status.isLocked) clearInterval(timer);
        }, 1000);
        return () => clearInterval(timer);
    }, [lockStatus.isLocked]);

    // 로그인 상태 유지 여부에 따라 토큰/사용자 정보 저장
    const saveAuthData = useCallback((
        rememberMe: boolean,
        accessToken: string | null | undefined,
        refreshToken: string | null | undefined,
        userInfo: Record<string, any>,
        userId: string,
        loginType: string,
    ) => {
        const cookieOptions = {
            path: '/',
            secure: window.location.protocol === 'https:',
            sameSite: 'lax' as const,
        };

        if (rememberMe) {
            localStorage.setItem('userId', userId);
            localStorage.setItem('loginType', loginType);
            localStorage.setItem('userInfo', JSON.stringify(userInfo));
            if (accessToken) {
                setCookie('accessToken', accessToken, { ...cookieOptions, expires: 30 });
            }
            if (refreshToken) {
                setCookie('refreshToken', refreshToken, { ...cookieOptions, expires: 30 });
            }
        } else {
            sessionStorage.setItem('userId', userId);
            sessionStorage.setItem('loginType', loginType);
            sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
            if (accessToken) {
                sessionStorage.setItem('accessToken', accessToken);
            }
            if (refreshToken) {
                sessionStorage.setItem('refreshToken', refreshToken);
            }
        }
    }, []);

    // 라이더 로그인 처리
    const handleRiderLogin = useCallback(async (userId: string, password: string, rememberMe: boolean) => {
        const request: LoginRequest = { userId, password };
        const response: LoginResponse = await login(request);

        if (response.success) {
            loginAttemptTracker.reset();
            setLockStatus(loginAttemptTracker.getStatus());

            // 비밀번호 미설정: saveAuthData/setAuth를 호출하지 않아 GuestRoute가
            // isLoggedIn=true로 판단해 delivery로 잘못 리다이렉트하는 것을 방지
            if (response.requiresPasswordSetup) {
                sessionStorage.setItem('userId', userId);
                setError(null);
                navigate('/password-setup');
                return;
            }

            // 정상 로그인 - 점검 확인 상태 초기화
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');

            const userInfo: any = {
                riderId: userId,
                requiresPasswordSetup: false,
                prmCode: response.prmCode,
            };

            if (response.userName) {
                userInfo.riderName = response.userName;
            }

            saveAuthData(rememberMe, response.accessToken, response.refreshToken, userInfo, userId, 'rider');

            // AuthContext에 인증 정보 저장 (suppType 포함)
            setAuth(
                userId,
                'RIDER',
                response.suppType || ''
            );

            // prmCode에 따라 라우팅
            if (response.prmCode === 'A') {
                navigate('/adminHome');
            } else {
                navigate('/home');
            }
        } else {
            const newStatus = loginAttemptTracker.recordFailure();
            setLockStatus(newStatus);

            const serverMessage = response.message;
            if (serverMessage?.includes('존재하지 않는') || serverMessage?.includes('비밀번호')) {
                setError('아이디 또는 비밀번호를 확인해주세요.');
            } else {
                setError(serverMessage || '아이디 또는 비밀번호를 확인해주세요.');
            }
        }
    }, [navigate, saveAuthData]);

    // 협력사 로그인 처리 (앱 API 사용)
    const handleSupplierLogin = useCallback(async (userId: string, password: string, rememberMe: boolean) => {
        const request: LoginRequest = { userId, password };
        const response: LoginResponse = await adminLogin(request);

        if (response.success) {
            loginAttemptTracker.reset();
            setLockStatus(loginAttemptTracker.getStatus());

            // 로그인 성공 시 점검 확인 상태 초기화
            sessionStorage.removeItem('maintenance_acknowledged');
            sessionStorage.removeItem('maintenance_acknowledged_seqno');

            const userInfo: any = {
                suppId: userId,
                userName: response.userName,
                prmCode: response.prmCode,
            };

            saveAuthData(rememberMe, response.accessToken, response.refreshToken, userInfo, userId, 'supplier');

            // AuthContext에 인증 정보 저장 (suppType 포함)
            setAuth(
                userId,
                'SUPPLIER',
                response.suppType || ''
            );

            // prmCode에 따라 라우팅
            if (response.prmCode === 'A') {
                navigate('/adminHome');
            } else {
                navigate('/delivery?tab=summary');
            }
        } else {
            const newStatus = loginAttemptTracker.recordFailure();
            setLockStatus(newStatus);

            const serverMessage = response.message;
            if (serverMessage?.includes('존재하지 않는') || serverMessage?.includes('비밀번호')) {
                setError('아이디 또는 비밀번호를 확인해주세요.');
            } else {
                setError(serverMessage || '아이디 또는 비밀번호를 확인해주세요.');
            }
        }
    }, [navigate, setAuth, saveAuthData]);

    // 통합 로그인 핸들러
    const handleLogin = useCallback(async (userId: string, password: string, type?: LoginType, rememberMe = false) => {
        const currentStatus = loginAttemptTracker.getStatus();
        if (currentStatus.isLocked) {
            const min = Math.floor(currentStatus.lockRemainingSeconds / 60);
            const sec = currentStatus.lockRemainingSeconds % 60;
            setError(`로그인이 잠겼습니다. ${min > 0 ? `${min}분 ` : ''}${sec}초 후 다시 시도해주세요.`);
            return;
        }

        setIsLoading(true);
        setError(null);

        const currentLoginType = type || loginType;

        try {
            if (currentLoginType === 'rider') {
                await handleRiderLogin(userId, password, rememberMe);
            } else {
                await handleSupplierLogin(userId, password, rememberMe);
            }
        } catch (err: any) {
            const errorMessage = err.message || err.response?.data?.message;
            if (errorMessage) {
                setError(errorMessage);
            } else {
                setError('아이디 또는 비밀번호를 확인해주세요.');
            }
        } finally {
            setIsLoading(false);
        }
    }, [loginType, handleRiderLogin, handleSupplierLogin]);

    return {
        handleLogin,
        isLoading,
        error,
        loginType,
        setLoginType,
        lockStatus,
    };
};

